// Authentication Context for ProView
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiFetch, { setSessionToken, clearSessionToken } from '../lib/apiFetch';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check existing session on mount
  const checkAuth = useCallback(async () => {
    try {
      const response = await apiFetch(`${API}/auth/me`);

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        if (response.status === 401) clearSessionToken();  // stale token
        setUser(null);
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Exchange session_id for user data (called by AuthCallback)
  const exchangeSession = async (sessionId) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiFetch(`${API}/auth/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ session_id: sessionId }),
      });

      if (response.ok) {
        const { session_token, ...userData } = await response.json();
        // Keep the session in app storage: the cookie is third-party here
        // (Vercel ↔ Railway) and Safari/iOS drop it.
        setSessionToken(session_token);
        setUser(userData);
        return { success: true, user: userData };
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Authentication failed');
        return { success: false, error: errorData.detail };
      }
    } catch (err) {
      console.error('Session exchange failed:', err);
      setError('Authentication service unavailable');
      return { success: false, error: 'Authentication service unavailable' };
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await apiFetch(`${API}/auth/logout`, { method: 'POST' });
    } catch (err) {
      console.error('Logout request failed:', err);
    } finally {
      clearSessionToken();
      setUser(null);
    }
  };

  // Login redirect to Google OAuth via our backend
  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `${BACKEND_URL}/api/auth/google`;
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    exchangeSession,
    checkAuth,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
